import React, { createContext, useState, useEffect, useContext } from 'react';
import api from '../utils/api';

// Create the authentication context
const AuthContext = createContext();

// Hook for components to get auth state and functions 
export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // On initial load, check if we have a token and fetch user data
  useEffect(() => {
    const checkLoggedIn = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('authToken');
        
        if (token) {
          // Get current user data using the api service
          // The api service automatically adds the Authorization header
        const response = await api.get('/auth/me');
          setCurrentUser(response.data);
        }
      } catch (err) {
        // If token is invalid or expired, clear it
        console.error('Auth check failed:', err);
        localStorage.removeItem('authToken');
      } finally {
        setLoading(false);
      }
    };
    
    checkLoggedIn();
  }, []);
  
  // Login function
  const login = async (email, password) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.post('/auth/login', { email, password });
      console.log('Login response:', response); // Debug log
      const { token, user } = response.data;
      
      if (!token) {
        console.error('No token received in login response');
        throw new Error('No token received from login');
      }
      
      // Save token and set user data
      localStorage.setItem('authToken', token);
      setCurrentUser(user);
      
      return user;
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed');
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  // No social login or registration - accounts are managed by admin only
  
  // Logout function
  const logout = () => {
    localStorage.removeItem('authToken');
    setCurrentUser(null);
    // No need to delete headers - our API service handles that
  };
  
  // Check if user has a specific role
  const hasRole = (role) => {
    return currentUser?.role === role || false;
  };
  
  // Check if user has a specific permission
  const hasPermission = (permission) => {
    return currentUser?.permissions?.[permission] || false;
  };
  
  // Context value
  const value = {
    currentUser,
    loading,
    error,
    login,
    logout,
    hasRole,
    hasPermission,
    isOwner: () => hasRole('Admin'),
    isTechnician: () => hasRole('Technician'),
    isUser: () => hasRole('User'),
    isAdmin: () => hasRole('Admin'),
    canManageUsers: () => hasRole('Admin') || hasPermission('manageUsers'),
    canManageOrganizations: () => hasRole('Admin') || hasPermission('manageOrganizations'),
    canManageLocations: () => hasRole('Admin') || hasPermission('manageLocations'),
    canManageDevices: () => hasRole('Admin') || hasPermission('manageDevices'),
    canManageTechnicians: () => hasRole('Admin') || hasPermission('manageTechnicians'),
    canViewReports: () => hasRole('Admin') || hasPermission('viewReports'),
    canViewBilling: () => hasRole('Admin') || hasPermission('viewBilling'),
    isAuthenticated: !!currentUser
  };
  
  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
